import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useCustomDispatch } from '../../hooks'
import { useAuthSelector } from '../../services/selectors/authSelector'

import { ValidateReturnedPatches } from '../../services/apis/dispense/validateReturnPatches'
import { SetReturnedPatchCount } from '../../services/slices/dispense/validateReturnPatches'
import { ErrorMessage } from './AllConfirmationMessages'

import { UploadTripilicatesPatientSigns } from './uploadTripilicatesPatientSigns'
import { GenrateRxLink } from '../../services/apis/dispense/genrateRxLink'
import { LoadingView } from './loader'

import { useAllSelector } from '../../services/selectors/allSelector'
import { useLocation } from 'react-router-dom'
import { NewRxesConsultationDropdown } from './deliveryConsultationDropdown'
import { FlexiProofUpload } from './flexiProofUpload'
import { CopayPaymentStatus } from './copayPaymentStatus'
import { removeVitalPatientById } from '../../services/slices/dispense/vitalPatients'

import VitalSignsEntry from './vitalSignsEntry'
import {  formatMinutesSecondsCompact, parseMinutesUntilCanDeliver } from '../../utils/helper/helper'
import { sanitizeHtml } from '../../utils/helper/security';
import { getDefaultUnconsumedDoseEntry, getUnconsumedDoseQuantityError, getUnconsumedDoseRxKey } from '../../utils/helper/unconsumedDose';
import { getDefaultDeliverRxIds, isRxFullyDelivered, isRxSelectableForDelivery, mergeDeliveredDosesForDisplay } from '../../utils/helper/deliveredRxDisplay';

export const DispenseRxData = ({ rxData,
	newRxCounsellingValues,
	setNewRxCounsellingValues,
	type = '',
	patientData,
	selectedDoses,
	setSelectedDoses,
	hideFlexi = true,
	flexiProofImages = {},
	setFlexiProofImages = () => { },
	setCopayPaymentOptions = () => { },
	setOpenIndex = () => { },
	showAddVitalButton = false,
	selectedDeliverRxIds = [],
	setSelectedDeliverRxIds = () => { },
	isActiveDeliverRxCard = true,
	deliverRxSelectionEnabled = true,
	unconsumedDoseEntries = {},
	setUnconsumedDoseEntries = () => { },
	unconsumedDoseErrors = {},
	setUnconsumedDoseErrors = () => { },
}) => {

	const [image, setImage] = useState('')
	// Blister Pack dropdown (Flexi tab): which packs are opened. Closed by default.
	const [openBlisterPacks, setOpenBlisterPacks] = useState({})
	const location = useLocation()?.pathname

	const dispatch = useCustomDispatch()
	const { driverID,
		//  hcpUser 
	} = useAuthSelector()

	// Handle copay payment status changes - memoized to prevent infinite loops
	const handleCopayPaymentChange = useCallback((winrxDataId, paymentData) => {
		if (setCopayPaymentOptions) {
			if (paymentData) {
				setCopayPaymentOptions(prev => ({
					...prev,
					[winrxDataId]: paymentData
				}));
			} else {
				setCopayPaymentOptions(prev => {
					const updated = { ...prev };
					delete updated[winrxDataId];
					return updated;
				});
			}
		}
	}, [setCopayPaymentOptions]);

	const allowedPages = ['/e-sign',
		'/flexi-delivery',
		'/driver-today-pending',
		'/add-vitals',
		'/driver-route-today',
		'/driver-today-missed-route',
		'/driver-missed-route'];

	const isAllowedPage = allowedPages.includes(location);
	// Only show deliver RX selection (checkboxes, etc.) on these pages
	const showDeliverRxSelection = isAllowedPage;

	// const isAddVitalsPage = showAddVitalButton ? true : hcpUser === 0 && location === '/add-vitals'
	const isAddVitalsPage = showAddVitalButton

	// Handle vitals success - remove patient from list
	const handleVitalsSuccess = useCallback((vitalsData) => {
		if (vitalsData?.patientId && isAddVitalsPage) {
			dispatch(removeVitalPatientById(vitalsData.patientId))
			setOpenIndex(null)
		}
	}, [isAddVitalsPage, dispatch, setOpenIndex])

	// Default-select only actionable RXs. Fully delivered and handed-over rows
	// remain visible as history but never enter the delivery selection.
	const rxDataKey = rxData?.length ? rxData.map((r) => r?.id).filter(Boolean).join(',') : ''
	useEffect(() => {
		if (!showDeliverRxSelection || !isActiveDeliverRxCard || !rxDataKey || !setSelectedDeliverRxIds) return
		setSelectedDeliverRxIds(getDefaultDeliverRxIds(rxData))
	}, [showDeliverRxSelection, isActiveDeliverRxCard, rxDataKey, setSelectedDeliverRxIds, rxData])

	// Blister pack (Flexi tab only): the drugs the blister sync marked Flexi (is_blister_flexi)
	// for this patient and the same route day go out together in one pack, so they are shown
	// together with one combined Collect amount and one proof image. Every other drug renders
	// exactly as before.
	const blisterPackKeyOf = (rx) => (
		location === '/flexi-delivery'
		&& rx?.is_blister_flexi === true
		&& Boolean(rx?.flexi_type)
		&& rx?.delivery_status === 'pending'
	) ? `blister-${patientData?.id ?? ''}-${rx?.flexi_delivery_date ?? ''}` : null

	// Keep each pack's drugs next to each other, at the place of its first drug.
	const groupBlisterPacks = (list = []) => {
		const placed = {}
		const ordered = list.reduce((acc, rx) => {
			const key = blisterPackKeyOf(rx)
			if (!key) return [...acc, rx]
			if (placed[key]) return acc
			placed[key] = true
			return [...acc, ...list.filter((other) => blisterPackKeyOf(other) === key)]
		}, [])
		// A patient with a pack: the pack(s) first, then the other drugs together in their own box.
		if (!ordered.some((rx) => blisterPackKeyOf(rx))) return ordered
		return [...ordered.filter((rx) => blisterPackKeyOf(rx)), ...ordered.filter((rx) => !blisterPackKeyOf(rx))]
	}

	// Ticked for delivery.
	const isRxTicked = (rx) => selectedDeliverRxIds?.includes(rx?.id)

	// The tick box on the Blister Pack heading ticks or unticks every drug in the pack together.
	const handleBlisterPackToggle = (packRxs) => {
		if (!setSelectedDeliverRxIds || !deliverRxSelectionEnabled) return
		const packIds = packRxs.filter(isRxSelectableForDelivery).map((rx) => rx?.id).filter((id) => id != null)
		const allTicked = packIds.length > 0 && packIds.every((id) => selectedDeliverRxIds?.includes(id))
		setSelectedDeliverRxIds((prev) => {
			const rest = (prev || []).filter((id) => !packIds.includes(id))
			return allTicked ? rest : [...rest, ...packIds]
		})
	}

	// One YES/NO for the pack, saved on every drug in it that has a copay (per drug, as before).
	const handleBlisterPackPaymentChange = (packRxs, paymentData) => {
		packRxs.forEach((rx) => {
			if (rx?.id == null || !(parseFloat(rx?.copay) > 0)) return
			handleCopayPaymentChange(rx?.id, paymentData ? { ...paymentData, winrx_data_id: rx?.id } : null)
		})
	}

	// One proof image for the pack, sent for every drug in it (the API stores proof per drug).
	const setBlisterPackProofImage = (packRxs, file) => {
		setFlexiProofImages((prev) => {
			const updated = { ...prev }
			packRxs.forEach((rx) => {
				if (rx?.id == null) return
				if (file === null) delete updated[rx?.rxnumber]
				else updated[rx?.rxnumber] = { id: rx?.id, file }
			})
			return updated
		})
	}

	// Put each row back together: a pack's heading, then its drug cards, then its footer.
	const assembleBlisterRows = (items) => {
		const list = items || []
		const out = []
		for (let i = 0; i < list.length; i++) {
			const item = list[i]
			if (!item.packKey) {
				out.push(<React.Fragment key={item.key}>{item.before}{item.row}{item.after}</React.Fragment>)
				continue
			}
			let j = i
			while (j + 1 < list.length && list[j + 1].packKey === item.packKey) j++
			const pack = list.slice(i, j + 1)
			out.push(
				<React.Fragment key={`pack-${item.key}`}>
					{item.before}
					<div>
						{pack.map((p) => <React.Fragment key={p.key}>{p.row}</React.Fragment>)}
					</div>
					{pack.map((p) => <React.Fragment key={`after-${p.key}`}>{p.after}</React.Fragment>)}
				</React.Fragment>
			)
			i = j
		}
		return out
	}

	return (
		<React.Fragment>
			{/* {patientData?.add_vital ? null : 
			(hcpUser === 0 && isAllowedPage && patientData?.id && 
				(patientData?.is_vital === 1 && ((driverID === patientData?.driver_hcp_id) || !patientData?.driver_hcp_id))) && (
				<VitalSignsEntry
					patientId={patientData?.id}
					onSuccess={handleVitalsSuccess}
					// onRemovePatient={(patientId) => dispatch(removeVitalPatientById(patientId))}
					isAddVitalsPage={isAddVitalsPage}
				/>
			)} */}

			{assembleBlisterRows(groupBlisterPacks(rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				}))
				?.map((value, index, list) => {
					const doseHistory = value?.investigate_dilaudid_delivery_history || value?.investigate_dilaudid_delivery_all
					const dilaudid_delivery =
						location === '/driver-today-missed-route' ? mergeDeliveredDosesForDisplay(value?.investigate_dilaudid_today_missed, doseHistory) :
							location === '/driver-missed-route' ? mergeDeliveredDosesForDisplay(value?.investigate_dilaudid_final_missed, doseHistory) :
								doseHistory

					const isEmergency = value?.rxsig ? value?.rxsig.toLowerCase().includes('emergency') : false;
					const isFlexiDelivery = value?.flexi_delivery
					const isFlexiDeliveryDate = value?.flexi_date
					const flexiType = value?.flexi_type
					const staleStatusColor = value?.flexi_stale_status === 'orange' ? '#ff4d01' : value?.flexi_stale_status === 'red' ? 'red' : '#31C48D'
					const showStaleStatus = !value?.flexi_expiry_date || Boolean(value?.flexi_stale_status)
					const blisterPackKey = blisterPackKeyOf(value)
					const isBlisterPackStart = Boolean(blisterPackKey) && blisterPackKeyOf(list[index - 1]) !== blisterPackKey
					// Other (non-blister) drugs of a patient with a pack: framed together in one box,
					// each drug's own layout unchanged.
					const isOtherRx = !blisterPackKey && list.some((rx) => blisterPackKeyOf(rx))
					const isOtherStart = isOtherRx && (index === 0 || Boolean(blisterPackKeyOf(list[index - 1])))
					const isOtherEnd = isOtherRx && index === list.length - 1
					const otherCount = isOtherStart ? list.filter((rx) => !blisterPackKeyOf(rx)).length : 0
					const isBlisterPackEnd = Boolean(blisterPackKey) && blisterPackKeyOf(list[index + 1]) !== blisterPackKey
					const blisterPackRxs = blisterPackKey ? list.filter((rx) => blisterPackKeyOf(rx) === blisterPackKey) : []
					const blisterPackSelectable = blisterPackRxs.filter((rx) => rx?.id != null && isRxSelectableForDelivery(rx))
					const blisterPackTicked = blisterPackSelectable.length > 0 && blisterPackSelectable.every(isRxTicked)
					// A patient with a pack: one Collect (all ticked drugs' copays) and one proof image,
					// after the last drug, for the pack and the other drugs together.
					const isCombinedFooter = index === list.length - 1 && list.some((rx) => blisterPackKeyOf(rx))
					const combinedRxs = isCombinedFooter ? list.filter((rx) => rx?.flexi_type && rx?.delivery_status === 'pending') : []
					const combinedCopay = combinedRxs.filter(isRxTicked).reduce((sum, rx) => sum + (parseFloat(rx?.copay) || 0), 0)
					const hasDeliveredParentStatus = String(value?.delivery_status || '').toLowerCase() === 'delivered'
					const isFullyDelivered = isRxFullyDelivered(value)
					const rxCanDeliver = value?.rx_access?.can_deliver !== false
					const assignedOwnerName = value?.assigned_rx_owner?.name
					// can_deliver logic only when status is not delivered: only when ALL doses have can_deliver === false → row disabled
					const allDosesCanDeliverFalse = !hasDeliveredParentStatus && dilaudid_delivery?.length > 0 &&
						dilaudid_delivery.every((d) => d?.can_deliver === false)
					const isParentChecked = showDeliverRxSelection && !isFullyDelivered && selectedDeliverRxIds?.includes(value?.id)
					const isCheckboxDisabled = showDeliverRxSelection && (!deliverRxSelectionEnabled || isFullyDelivered || !rxCanDeliver || allDosesCanDeliverFalse)
					const rxKey = getUnconsumedDoseRxKey(value)
					const unconsumedEntry = unconsumedDoseEntries?.[rxKey] || getDefaultUnconsumedDoseEntry()
					const unconsumedEntryErrors = unconsumedDoseErrors?.[rxKey] || {}
					const unconsumedDisabled = showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)

					const handleDeliverRxToggle = () => {
						if (value?.id == null || !setSelectedDeliverRxIds || !deliverRxSelectionEnabled || isFullyDelivered || !rxCanDeliver) return
						if (allDosesCanDeliverFalse) return
						const willBeChecked = !selectedDeliverRxIds?.includes(value.id)
						setSelectedDeliverRxIds(prev => {
							const next = prev ? [...prev] : []
							const idx = next.indexOf(value.id)
							if (idx === -1) next.push(value.id)
							else next.splice(idx, 1)
							return next
						})
						if (willBeChecked && dilaudid_delivery?.length > 0) {
							// Select all doses that have can_deliver === true (same as DoseFrequencySelector default)
							const deliverableDoses = dilaudid_delivery
								.filter((d) => d?.can_deliver === true)
								.map((d) => ({ frequency: d?.frequency, dose_id: d?.id, dose_no: d?.dose_no, dose_gap_prompt: d?.dose_gap_prompt }))
							setSelectedDoses(prev => ({
								...prev,
								[value.rxnumber]: deliverableDoses.length > 0 ? deliverableDoses : [],
							}))
						} else if (!willBeChecked) {
							setSelectedDoses(prev => ({ ...prev, [value.rxnumber]: [] }))
						}
					}

					const handleUnconsumedToggle = (enabled) => {
						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: enabled ? {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
							} : getDefaultUnconsumedDoseEntry(),
						}))

						setUnconsumedDoseErrors((prevState) => {
							if (!prevState?.[rxKey]) return prevState
							const nextState = { ...prevState }
							delete nextState[rxKey]
							return nextState
						})
					}

					const handleUnconsumedQuantityChange = (inputValue) => {
						const quantityError = getUnconsumedDoseQuantityError({
							rx: value,
							selectedDoses,
							quantity: inputValue,
						})

						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
								quantity: inputValue,
							},
						}))

						setUnconsumedDoseErrors((prevState) => ({
							...prevState,
							[rxKey]: {
								...(prevState?.[rxKey] || {}),
								quantity: quantityError,
							},
						}))
					}

					const handleUnconsumedSafetyStatusChange = (safetyStatus) => {
						setUnconsumedDoseEntries((prevState) => ({
							...prevState,
							[rxKey]: {
								...getDefaultUnconsumedDoseEntry(),
								...(prevState?.[rxKey] || {}),
								enabled: true,
								safetyStatus,
							},
						}))

						setUnconsumedDoseErrors((prevState) => ({
							...prevState,
							[rxKey]: {
								...(prevState?.[rxKey] || {}),
								safetyStatus: '',
							},
						}))
					}

					// Split into heading / drug card / footer, so a pack's cards can share one scroll box.
					return {
						key: index,
						packKey: blisterPackKey,
						before: (<React.Fragment>
							{isBlisterPackStart && (
								<div
									className="mt-3 flex cursor-pointer select-none items-center gap-2 rounded-t-md border border-b-0 border-teal-600 bg-teal-50 px-3 py-2"
									role="button"
									aria-expanded={Boolean(openBlisterPacks[blisterPackKey])}
									onClick={() => setOpenBlisterPacks((prev) => ({ ...prev, [blisterPackKey]: !prev[blisterPackKey] }))}
								>
									{showDeliverRxSelection && (
										<label
											className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${!deliverRxSelectionEnabled ? 'opacity-50 bg-gray-100 text-gray-500 cursor-not-allowed border-gray-100' : blisterPackTicked ? 'cursor-pointer border-blue-600 bg-blue-600 hover:bg-blue-700 text-white' : 'cursor-pointer border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
											onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleBlisterPackToggle(blisterPackRxs); }}
										>
											<input
												type="checkbox"
												checked={blisterPackTicked}
												onChange={(e) => { e.stopPropagation(); handleBlisterPackToggle(blisterPackRxs); }}
												disabled={!deliverRxSelectionEnabled}
												className="sr-only"
												tabIndex={-1}
											/>
											{blisterPackTicked && (
												<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
													<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
												</svg>
											)}
										</label>
									)}
									{/* Same shape as the "Show Instructions" button (rounded, 2px border), at its own size. */}
									<div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-teal-600 bg-white py-1 px-2.5 text-xs font-semibold text-teal-700">
										<svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
											<rect x="1.5" y="1.5" width="13" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
											<circle cx="5.5" cy="5.5" r="1.6" fill="currentColor" />
											<circle cx="10.5" cy="5.5" r="1.6" fill="currentColor" />
											<circle cx="5.5" cy="10.5" r="1.6" fill="currentColor" />
											<circle cx="10.5" cy="10.5" r="1.6" fill="currentColor" />
										</svg>
										Blister Pack
										<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[11px] font-semibold">
											{blisterPackRxs.length}
										</span>
									</div>
									<svg className={`ml-auto h-5 w-5 shrink-0 text-teal-700 transition-transform duration-200 ${openBlisterPacks[blisterPackKey] ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
										<path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
									</svg>
								</div>
							)}
							{isOtherStart && (
								<div className="mt-3 flex items-center rounded-t-md border border-b-0 border-slate-300 bg-slate-50 px-3 py-2">
									<div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-slate-400 bg-white py-1 px-2.5 text-xs font-semibold text-slate-700">
										Regular Medications
										<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-500 text-white text-[11px] font-semibold">
											{otherCount}
										</span>
									</div>
								</div>
							)}
						</React.Fragment>),
						row: (
							<div className={`${blisterPackKey ? `border-x border-teal-600 bg-teal-50 px-2 py-1 ${openBlisterPacks[blisterPackKey] ? '' : 'hidden'}` : isOtherRx ? `border-x border-slate-300 bg-slate-50 px-2 py-1 ${isOtherEnd ? 'mb-3 rounded-b-md border-b pb-2' : ''}` : 'mt-2 mb-2'} break-all ${allDosesCanDeliverFalse ? 'opacity-60' : ''}`}>
								<div
									className="rounded-md border border-gray-200 bg-white p-2"
									style={value?.is_din_matched?.status === 1 ? { backgroundColor: value?.is_din_matched?.color || 'white' } : undefined}
								>

									<div className='flex gap-2 justify-between items-center flex-wrap'>
										{/* flex-wrap: the row can now carry the Rx number, "Delivered"
										    AND "Witness Required" at once (tickets 2026-08-07-001 stopped
										    hiding completed rows). Without wrapping the badges overflow
										    and paint on top of the Rx number on a phone-width screen. */}
										<div className='flex gap-2 items-center flex-wrap'>
											{showDeliverRxSelection && !blisterPackKey && (
												<label
													className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${isCheckboxDisabled ? 'opacity-50 bg-gray-100 text-gray-500 cursor-not-allowed border-gray-100' : isParentChecked ? 'cursor-pointer border-blue-600 bg-blue-600 hover:bg-blue-700 text-white' : 'cursor-pointer border-gray-100 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
													onClick={(e) => {
														if (isCheckboxDisabled) {
															e.preventDefault();
															return;
														}
														e.preventDefault();
														handleDeliverRxToggle();
													}}
												>
													<input
														type="checkbox"
														checked={!!isParentChecked}
														onChange={(e) => { e.stopPropagation(); handleDeliverRxToggle(); }}
														disabled={isCheckboxDisabled}
														className="sr-only"
														tabIndex={-1}
													/>
													{isParentChecked && (
														<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
													)}
												</label>
											)}
											{/* whitespace-nowrap on every badge: the row wrapper carries
											    `break-all` for the long SIG text, which was also breaking
											    these short labels mid-word -- "Deliver ed", "Witness Requ ired". */}
											<p className="text-sm font-normal text-white rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>
												{value?.rxnumber}
											</p>

											{isFullyDelivered && (
												<p className="text-sm font-normal text-white rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: '#0F766E', paddingLeft: '10px', paddingRight: '10px' }}>
													Delivered
												</p>
											)}

											{value.rx_witness === 'Y' &&
												<p className="text-sm font-normal text-white bg-yellow-500 rounded-md p-1 whitespace-nowrap" style={{ fontSize: '12px', height: '29px', background: '#6A057B', paddingLeft: '10px', paddingRight: '10px' }}>
													Witness Required
												</p>}

											<GenrateRxNumberDataPDF value={value} />

											{value?.is_triplicates_pending?.is_triplicates &&
												<div className='text-sm font-normal text-white bg-red-500 rounded-md' onClick={() => setImage(value?.is_triplicates_pending?.precription_image)}>
													<UploadTripilicatesPatientSigns
														index={index}
														rxData={value}
														image={image} />
												</div>
										}
									</div>

									{
											isFlexiDelivery && !blisterPackKey &&
											<div className='flex gap-1 flex-wrap'>
												{hideFlexi &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														FLEXI
													</div>}

												{
													!hideFlexi && isFlexiDeliveryDate &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{isFlexiDeliveryDate}
													</div>
												}

												{flexiType && (
													<div style={{ height: '25px' }} className={`text-xs font-normal ${flexiType === 'delivery' ? 'bg-blue-500' : 'bg-purple-500'} text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{flexiType.toUpperCase()}
													</div>
												)}

												{showStaleStatus && (
													<div style={{ height: '25px', background: staleStatusColor }} className={`text-xs font-normal text-white  px-4 py-1 flex items-center h-fit justify-center rounded-full`}>
														STALE
													</div>
												)}
											</div>
										}
									</div>

									<h3 className="text-sm font-medium my-1">
										[{value.refill_reqty}] {value.drug}
									</h3>

									<p className="text-sm text-gray-900">{value?.rxsig}</p>

									<p className='pt-1' dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.prescriptionExpiryDay) }} />
									{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}

									{patientData?.delivery_status !== 'delivered' && value?.is_din_matched?.is_fentanyl === 1
										&& patientData?.patients_win_rx_data?.length > 0 &&
										<div className={showDeliverRxSelection && patientData && !selectedDeliverRxIds?.includes(value?.id) ? 'opacity-50 pointer-events-none' : ''}>
											<ReturnedPatches
												index={index}
												rxData={value}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>}
									<InvestageRxPDF value={value} />

									{type === 'pending_counselling_rxes' &&
										<div>
											{value?.drug_counselling_sheet &&
												<div className='mt-2 border border-gray-400 rounded-md p-2 text-sm h-80  bg-white overflow-y-auto pending-counselling-sheet'
													dangerouslySetInnerHTML={{ __html: sanitizeHtml(value?.drug_counselling_sheet) }}
												>
												</div>
											}
											<div className='mt-2'>
												<NewRxesConsultationDropdown
													type={type}
													counsellingValues={newRxCounsellingValues}
													rxData={value}
													setCounsellingValues={setNewRxCounsellingValues}
													disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)} />
											</div>
										</div>
									}

									{ value?.counsling_provided === 1 ? null :
										value?.is_new_rx && type === 'new_rx' && !isEmergency && (patientData ? patientData?.delivery_status !== 'delivered' : value?.delivery_status !== 'delivered') &&
										<div className='mt-2'>
											<NewRxesConsultationDropdown
												type={type}
												counsellingValues={newRxCounsellingValues}
												rxData={value}
												setCounsellingValues={setNewRxCounsellingValues}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>
									}


									<DoseFrequencySelector
										dilaudidDelivery={dilaudid_delivery}
										selectedDoses={selectedDoses[value?.rxnumber] || []}
										setSelectedDoses={(doses) =>
											setSelectedDoses((prev) => ({
												...prev,
												[value?.rxnumber]: doses,
											}))
										}
										previous_delivery_time_diff={value?.previous_delivery_time_diff}
										minutes_until_can_deliver={value?.minutes_until_can_deliver ?? value?.investigate_dilaudid_delivery_all?.[0]?.minutes_until_can_deliver}
										disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
										winrxDataId={value?.id}
										onAllDosesUnavailable={(id) => setSelectedDeliverRxIds((prev) => {
											const current = prev || []
											return current.includes(id) ? current.filter((x) => x !== id) : current
										})}
									/>

									{String(value?.delivery_status || '').toLowerCase() !== 'delivered' && !blisterPackKey && (
										<div className={`mt-1 rounded-lg border border-gray-200 bg-white p-1 ${unconsumedDisabled ? 'opacity-60' : ''}`}>
											<div className="flex items-center justify-between gap-3" 
													onClick={() => handleUnconsumedToggle(!Boolean(unconsumedEntry?.enabled))}
											
											>
												<div className="min-w-0">
													<p className="text-sm font-medium text-gray-900">Unconsumed / Refused Dose</p>
												</div>
												<button
													type="button"
													disabled={unconsumedDisabled}
													aria-expanded={Boolean(unconsumedEntry?.enabled)}
													aria-label={Boolean(unconsumedEntry?.enabled) ? 'Hide unconsumed or refused dose details' : 'Show unconsumed or refused dose details'}
													onClick={() => handleUnconsumedToggle(!Boolean(unconsumedEntry?.enabled))}
													// className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-700 transition ${unconsumedDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:border-primary hover:text-primary'}`}
												>
													<svg
														className={`h-4 w-4 transition-transform ${unconsumedEntry?.enabled ? 'rotate-90' : ''}`}
														viewBox="0 0 20 20"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
														aria-hidden="true"
													>
														<path
															d="M7.5 5.83325L11.6667 9.99992L7.5 14.1666"
															stroke="currentColor"
															strokeWidth="1.8"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</button>
											</div>

											{unconsumedEntry?.enabled && (
												<div className="mt-2 space-y-3">
													<div className="grid grid-cols-[80px_1fr] gap-0.5 items-start">
														<input
															type="number"
															min="0"
															step="any"
															inputMode="decimal"
															value={unconsumedEntry?.quantity ?? ''}
															onChange={(event) => handleUnconsumedQuantityChange(event.target.value)}
															placeholder="Qty"
															className={`block w-full h-9 rounded-lg border bg-white px-2 text-xs text-gray-900 outline-0 ${unconsumedEntryErrors?.quantity ? 'border-red-500' : 'border-gray-300'} focus:border-primary`}
														/>
														<div className="flex h-9 items-center justify-start gap-3 rounded-lg border border-gray-200 bg-white px-2">
															<label className="flex items-center gap-1 cursor-pointer">
																<input
																	type="radio"
																	name={`unconsumed-dose-status-${rxKey}`}
																	checked={unconsumedEntry?.safetyStatus === 'safe'}
																	onChange={() => handleUnconsumedSafetyStatusChange('safe')}
																	className="h-4 w-4 border-gray-300 text-[#10b981] focus:ring-[#10b981]"
																/>
																<span className="text-xs text-gray-800">Un-tampered</span>
															</label>

															<label className="flex items-center gap-1 cursor-pointer">
																<input
																	type="radio"
																	name={`unconsumed-dose-status-${rxKey}`}
																	checked={unconsumedEntry?.safetyStatus === 'unsafe'}
																	onChange={() => handleUnconsumedSafetyStatusChange('unsafe')}
																	className="h-4 w-4 border-gray-300 text-[#10b981] focus:ring-[#10b981]"
																/>
																<span className="text-xs text-gray-800">Tampered</span>
															</label>
														</div>
													</div>

													{unconsumedEntryErrors?.quantity ? (
														<p className="text-xs text-red-600">{unconsumedEntryErrors.quantity}</p>
													) : null}

													{unconsumedEntryErrors?.safetyStatus ? (
														<p className="text-xs text-red-600">{unconsumedEntryErrors.safetyStatus}</p>
													) : null}
												</div>
											)}
										</div>
									)}

									{!rxCanDeliver && !isFullyDelivered && (
										<p className="mt-2 w-full min-w-0 rounded-md bg-slate-700 px-2 py-2 text-[11px] font-medium leading-tight text-white whitespace-normal" style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}>
											{assignedOwnerName ? `Assigned to ${assignedOwnerName}` : 'Assigned to another user'}
										</p>
									)}

									{/* Copay Payment Status Component */}
									{flexiType && value?.delivery_status === 'pending' && !blisterPackKey && !isOtherRx && (
										<CopayPaymentStatus
											rxData={value}
											index={index}
											rxnumber={value?.rxnumber}
											copayValue={value?.copay}
											winrxDataId={value?.id}
											onPaymentChange={handleCopayPaymentChange}
										/>
									)}

									{/* Flexi Proof Upload - Show only if flexiType exists */}
									{flexiType && value?.delivery_status === 'pending' && !blisterPackKey && !isOtherRx && (
										<FlexiProofUpload
											rxData={value}
											index={index}
											rxnumber={value?.rxnumber}
											rxId={value?.id}
											proofImage={flexiProofImages[value?.rxnumber]?.file || flexiProofImages[value?.rxnumber] || null}
											setProofImage={(file) => {
												setFlexiProofImages((prev) => {
													if (file === null) {
														// Remove the key when file is null
														const updated = { ...prev };
														delete updated[value?.rxnumber];
														return updated;
													}
													return {
														...prev,
														[value?.rxnumber]: {
															id: value?.id,
															file: file
														}
													};
												});
											}}
										/>
									)}

								</div>
							</div>
						),
						after: (<React.Fragment>
							{isBlisterPackEnd && (
								<div className="mb-3 h-2 rounded-b-md border border-t-0 border-teal-600 bg-teal-50" />
							)}
							{isCombinedFooter && combinedRxs.length > 0 && (
								<div className="mb-3 rounded-md border border-gray-200 bg-white p-2">
									{/* One Collect for everything ticked: the pack and the other drugs' copays added up. */}
									<CopayPaymentStatus
										rxData={value}
										index={index}
										rxnumber={`all-${patientData?.id ?? ''}`}
										copayValue={combinedCopay}
										winrxDataId={`all-${patientData?.id ?? ''}`}
										onPaymentChange={(_, paymentData) => handleBlisterPackPaymentChange(combinedRxs, paymentData)}
									/>
									{/* One proof image for all of them. */}
									<FlexiProofUpload
										rxData={value}
										index={index}
										rxnumber={`all-${patientData?.id ?? ''}`}
										rxId={value?.id}
										proofImage={flexiProofImages[combinedRxs.find((rx) => rx?.id != null)?.rxnumber]?.file || null}
										setProofImage={(file) => setBlisterPackProofImage(combinedRxs, file)}
									/>
								</div>
							)}
						</React.Fragment>),
					}
				}))
			}

			{/* Vital Signs Entry - Show if patientData is provided and has an id */}
			{patientData?.add_vital ? null :
				(isAllowedPage && patientData?.id && (patientData?.is_vital === 1 &&
					((driverID === patientData?.driver_hcp_id) || !patientData?.driver_hcp_id))) && (
					<VitalSignsEntry
						patientId={patientData?.id}
						onSuccess={handleVitalsSuccess}
						// onRemovePatient={(patientId) => dispatch(removeVitalPatientById(patientId))}
						isAddVitalsPage={isAddVitalsPage}
					/>
				)}
		</React.Fragment>
	)
}

// Genrate Rx Number Data PDF
export const GenrateRxNumberDataPDF = ({ value }) => {

	const { pharmacyId } = useAuthSelector()
	const { GenrateRxLinkResponse } = useAllSelector()
	const dispatch = useCustomDispatch()

	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)

	const handleGenrateRxLink = (value) => {
		setLoading(true)
		const requestedData = {
			pharmacy_id: pharmacyId,
			rxnum: value.rxnumber,
			rx_br: value.rx_br
		}
		dispatch(GenrateRxLink({ requestedData, setShowToast, setLoading }))
	}

	return (
		<React.Fragment>
			{value?.rx_br &&
				<p className="p-1" onClick={() => handleGenrateRxLink(value)}>
					<svg xmlns="http://www.w3.org/2000/svg" className='w-6 h-6' viewBox="0 0 64 64" width="512" height="512"><g id="File"><path d="M18,3V24H8V5a2.006,2.006,0,0,1,2-2Z" style={{ fill: '#bcbec0' }} /><path d="M25,3V25H14V5a2.006,2.006,0,0,1,2-2Z" style={{ fill: '#d1d3d4' }} /><path d="M56,5V25H20V5a2.006,2.006,0,0,1,2-2H54A2.006,2.006,0,0,1,56,5Z" style={{ fill: '#e6e7e8' }} /><path d="M61,27V57a4,4,0,0,1-4,4H7a4,4,0,0,1-4-4V19a2.006,2.006,0,0,1,2-2H24.22a1,1,0,0,1,.97.76l1.62,6.48a1,1,0,0,0,.97.76H59A2.006,2.006,0,0,1,61,27Z" style={{ fill: '#ffa733' }} /><circle cx="32" cy="43" r="10" style={{ fill: '#ffd422' }} /><polygon points="38 41 38 45 34 45 34 49 30 49 30 45 26 45 26 41 30 41 30 37 34 37 34 41 38 41" style={{ fill: '#ff5023' }} /><polygon points="52 3 52 11 49 9 46 11 46 3 52 3" style={{ fill: '#ff5023' }} /><path d="M41.707,13.707l-1.414-1.414L38,14.586l-.826-.826A3,3,0,0,0,36,8H32a1,1,0,0,0-1,1v8h2V14h1.586l2,2-2.293,2.293,1.414,1.414L38,17.414l2.293,2.293,1.414-1.414L39.414,16ZM36,12H33V10h3a1,1,0,0,1,0,2Z" style={{ fill: '#1e81ce' }} /></g></svg>
				</p>}

			{GenrateRxLinkResponse?.loading && loading &&
				<div className="fixed top-0 left-0 w-full h-full  opacity-20 flex justify-center items-center z-50">
					<LoadingView />
				</div>}

			{showToast &&
				<div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50">
					<ErrorMessage message={'Oops, we got an issue when loading the PDF. Please try again later.'} />
				</div>}

		</React.Fragment>
	)
}

// Investage Rx PDF
export const InvestageRxPDF = ({ value }) => {

	const dispatch = useCustomDispatch()

	const { pharmacyId } = useAuthSelector()
	const { GenrateRxLinkResponse } = useAllSelector()

	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)

	const handleGenrateRxLink = (value) => {
		const requestedData = {
			pharmacy_id: pharmacyId,
			rxnum: value.rxnumber,
			rx_br: value.rx_br
		}
		setLoading(true)
		dispatch(GenrateRxLink({ requestedData, setShowToast, setLoading }))
	}

	return (
		<React.Fragment>

			{value?.pharmacyWinrxRxEs ?
				value?.pharmacyWinrxRxEs?.signature_required === 1 && value?.pharmacyWinrxRxEs?.signature_complete === 0 ?

					<div className="mt-1 rounded-md investageRx whitespace-nowrap text-white pl-2 p-1" style={{ fontSize: '12px' }}>
						Patient signature required for this <span style={{ textDecoration: 'underline' }} onClick={() => handleGenrateRxLink(value)}>prescription</span>
					</div>

					: value?.pharmacyWinrxRxEs?.signature_complete === 1 &&
					<p className="mt-1 rounded-md investageRx whitespace-nowrap text-white pl-2 p-1" style={{ fontSize: '12px' }}>
						Prescription signed on {value?.pharmacyWinrxRxEs?.signature_date}
					</p>
				: null
			}

			{GenrateRxLinkResponse?.loading && loading &&
				<div className="fixed top-0 left-0 w-full h-full bg-gray-800 opacity-20 flex justify-center items-center z-50">
					<LoadingView />
				</div>}

			{showToast &&
				<div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50">
					<ErrorMessage message={'Oops, we got an issue when loading the PDF. Please try again later.'} />
				</div>}

		</React.Fragment>
	)
}

// Counselling New Rx Data
export const CounsellingNewRxData = ({ rxData,
	counsellingOption,
	handleRadioChange,
	patient_name
}) => {

	const radioOptions = [
		{ value: 'Yes', label: <><strong>Yes,</strong> Counselling Provided</> },
		{ value: 'No', label: <><strong>No,</strong> Counselling not Provided</> },
		{ value: 'Refused', label: <>Patient <strong>Refused</strong> Counselling</> }
	];

	return (
		<React.Fragment>
			{rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				})
				?.map((value, index) => (
					<React.Fragment key={index}>
						<div className="mt-2 mb-2 break-all">
							<div
								className="rounded-md border border-gray-200 bg-white p-2"
								style={value?.is_din_matched?.status === 1 ? { backgroundColor: value?.is_din_matched?.color || 'white' } : undefined}
							>
								<span className="text-sm font-normal text-white bg-[#10b981] rounded-md p-1" style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>
									{value?.rxnumber}
								</span>
								<h3 className="text-sm font-medium my-1">
									[{value?.refill_reqty}]{value?.drug}
								</h3>
								<p className="text-sm text-gray-900">{value?.rxsig}</p>
								<p className="pt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.prescriptionExpiryDay) }} />
								{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}
							</div>
						</div>

						{radioOptions?.map((option, idx) => (
							<div key={idx} className="text-sm mt-1 bg-gray-50 rounded-md px-3 py-2 flex items-center gap-2 uppercase w-full">
								<input
									type="radio"
									name={`counselling-${index}`}
									value={option.value}
									checked={counsellingOption[index] === option.value}
									onChange={(e) => handleRadioChange(e, index, value, patient_name)}
									className="outline-none"
									id={`${option.value.toLowerCase()}-${index}`}
									style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px' }}
								/>
								<label htmlFor={`${option.value.toLowerCase()}-${index}`}>{option.label}</label>
							</div>
						))}

					</React.Fragment>
				))}
		</React.Fragment>
	)
}

// Pending Pharmacist Sign Rx Data
export const PendingPharmacistSignRxData = ({ rxData, selectedDoses, setSelectedDoses }) => {

	return (
		<React.Fragment>
			{rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				})?.map((value, index) => {
					// console.log(value)
					const isFlexiDelivery = value?.flexi_delivery
					const flexiType = value?.flexi_type
					const staleStatusColor = value?.flexi_stale_status === 'orange' ? '#ff4d01' : value?.flexi_stale_status === 'red' ? 'red' : '#31C48D'
					const showStaleStatus = !value?.flexi_expiry_date || Boolean(value?.flexi_stale_status)
					return (
						<React.Fragment key={index}>
							{value?.is_din_matched?.status === 0 ? null :
								<div className="mt-2 mb-2 break-all">
									<div
										className="rounded-md border border-gray-200 bg-white p-2"
										style={value?.is_din_matched?.status === 1 ? { backgroundColor: value?.is_din_matched?.color || 'white' } : undefined}
									>
										<div className='flex flex-wrap gap-2 justify-between items-center'>

											<div className='flex'>
												<span className="text-sm font-normal text-white bg-[#10b981] rounded-md p-1"
													style={{
														fontSize: '12px',
														height: '29px',
														background: 'black',
														paddingLeft: '10px',
														paddingRight: '10px'
													}}>{value?.rxnumber}</span>

												<span className='text-xs  bg-gray-50 rounded-md px-3 py-1 flex items-center gap-2 uppercase ml-2'>
													<input type='checkbox' className='outline-none'
														id={`signatures-${index}`} style={{
															outline: 'none',
															border: '0.1px solid gray',
															borderRadius: '4px',
														}} />
													<label htmlFor={`signatures-${index}`}>
														Ignore
													</label>
												</span>
											</div>

											{
												isFlexiDelivery &&
												<div className='flex gap-1'>
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														FLEXI
													</div>
													{flexiType && (
														<div style={{ height: '25px' }} className={`text-xs font-normal ${flexiType === 'delivery' ? 'bg-blue-500' : 'bg-purple-500'} text-white px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
															{flexiType.toUpperCase()}
														</div>
													)}
													{showStaleStatus && (
														<div style={{ height: '25px', background: staleStatusColor }} className={`text-xs font-normal text-white px-4 py-1 flex items-center h-fit justify-center rounded-full`}>
															STALE
														</div>
													)}
												</div>
											}
										</div>

										<h3 className="text-sm font-medium my-1">
											{/* [{value?.refill_reqty}]{value?.drug} */}

											{/* {Array.isArray(value?.investigate_dilaudid_delivery) && value?.investigate_dilaudid_delivery ?
											value?.investigate_dilaudid_delivery?.map((dilaudidDelivery, dilaudidIndex) => {
												return (
													<React.Fragment key={dilaudidIndex}>
														{dilaudidDelivery.delivery_status === 'delivered' ?
															<p>
																{dilaudidDelivery?.dose_no ? `[${dilaudidDelivery?.dose}] ` : ''}
																{value.drug}
																{dilaudidDelivery?.frequency && {
																	'QID': `[${dilaudidDelivery?.dose_no}/4]`,
																	'TID': `[${dilaudidDelivery?.dose_no}/3]`,
																	'BID': `[${dilaudidDelivery?.dose_no}/2]`
																}[dilaudidDelivery?.frequency]} [TQTY {value?.refill_reqty}]
															</p>
															: <>[{value.refill_reqty}] {value.drug}</>}
													</React.Fragment>
												)
											})
											:
											value?.investigate_dilaudid_delivery ? (
												<>
													[{value?.investigate_dilaudid_delivery.dose}] {value.drug}
													{value?.investigate_dilaudid_delivery?.frequency && {
														'QID': `[${value?.investigate_dilaudid_delivery?.dose_no}/4]`,
														'TID': `[${value?.investigate_dilaudid_delivery?.dose_no}/3]`,
														'BID': `[${value?.investigate_dilaudid_delivery?.dose_no}/2]`
													}[value?.investigate_dilaudid_delivery?.frequency]} [TQTY {value?.refill_reqty}]
												</>
											) : (
												<>[{value.refill_reqty}] {value.drug}</>
											)} */}

											[{value?.refill_reqty}] {value?.drug}


											<DoseFrequencySelector
												dilaudidDelivery={value?.investigate_dilaudid_delivery_all}
												// dilaudidDelivery={dilaudid_delivery}
												// setSelectedDoses={setSelectedDoses}
												// selectedDoses={selectedDoses}
												selectedDoses={selectedDoses[value?.rxnumber] || []}
												setSelectedDoses={(doses) =>
													setSelectedDoses((prev) => ({
														...prev,
														[value?.rxnumber]: doses, // update only this rx
													}))
												}
											// previous_delivery_time_diff={value?.previous_delivery_time_diff}
											/>




										</h3>
										<p className="text-sm text-gray-900">{value?.rxsig}</p>
										<p className='pt-1' dangerouslySetInnerHTML={{ __html: sanitizeHtml(value?.prescriptionExpiryDay) }} />
										{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}
									</div>
								</div>}
						</React.Fragment>
					)
				})}
		</React.Fragment>
	)
}

// Returned Patches
export const ReturnedPatches = ({ index, rxData, disabled = false }) => {

	const [selectedValue, setSelectedValue] = useState(null);
	const [returnedCount, setReturnedCount] = useState('');
	// After "Yes": 'all' (every expected patch came back) or 'other' (Partial: type the count).
	const [returnMode, setReturnMode] = useState('');
	const dispatch = useCustomDispatch()
	const [showToast, setShowToast] = useState({ successToast: false, errorToast: false })
	const previousPatchDeliveryTime = rxData?.previous_patch_delivery_time_display;
	const showPreviousPatchDeliveryTime = Boolean(rxData?.previous_patch_id && previousPatchDeliveryTime);

	const { pharmacyId } = useAuthSelector()

	const handleAnswer = (value) => {
		if (disabled || value === selectedValue) return;
		setSelectedValue(value)
		// "Yes" starts on All (the expected count); Other is there for a short return.
		const startOnAll = value === 'yes' && expectedCount > 0
		const count = startOnAll ? String(expectedCount) : ''
		setReturnedCount(count)
		setReturnMode(startOnAll ? 'all' : '')
		const requestedData = {
			pharmacy_id: pharmacyId,
			patient_id: rxData.patient_id,
			rxnumber: rxData.rxnumber,
			is_return: value,
			patients_win_rx_data_id: rxData.id,
			setShowToast: setShowToast
		}
		// The expected count shown here goes with the answer, so the pharmacy keeps the
		// same number the driver counted against (e.g. 28 expected, 20 back = 8 missing).
		dispatch(SetReturnedPatchCount({ winrxDataId: rxData.id, count, expected: expectedCount, latestAnswer: value }))
		dispatch(ValidateReturnedPatches(requestedData))
	};

	// Most that can come back: the patches handed out last time (from the server).
	// No previous fill found: this fill's quantity, the [N] shown on the card.
	const previousQty = rxData?.previous_patch_qty;
	const cardQty = Math.round(Number(rxData?.refill_reqty));
	const expectedCount = previousQty !== null && previousQty !== undefined && Number.isFinite(Number(previousQty))
		? Number(previousQty)
		: (Number.isFinite(cardQty) && cardQty > 0 ? cardQty : null);
	const overExpected = expectedCount !== null && returnedCount !== '' && Number(returnedCount) > expectedCount;

	// Whole numbers only (digits, so no minus or decimal); sent with the delivery and
	// shown in the pharmacy's patch log.
	const saveCount = (count) => {
		setReturnedCount(count)
		dispatch(SetReturnedPatchCount({ winrxDataId: rxData.id, count, expected: expectedCount }))
	};
	const handleCountChange = (e) => {
		saveCount(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 3))
	};
	// "All" fills in the expected count; "Partial" clears it for the driver to type.
	const handleModeChange = (mode) => {
		if (disabled || mode === returnMode) return;
		setReturnMode(mode)
		const count = mode === 'all' && expectedCount !== null ? String(expectedCount) : ''
		// Only touch the shared store when the count really changes: every store
		// update redraws the whole delivery screen, which makes the card flicker.
		if (count === returnedCount) return;
		saveCount(count)
	};

	return (
		<React.Fragment>
			<div style={{ position: 'relative' }} className=''>


				<div style={{ border: '1px solid lightgray' }} className={`text-xs font-semibold mt-1 bg-gray-50 rounded-md px-1.5   gap-2 uppercase ${disabled ? 'opacity-60' : ''}`}>

					<div className='text-xs font-semibold pt-2 rounded-md px-0 py-0 flex items-center justify-between gap-2 normal-case'>
						<label>
							Are previous patches returned?
						</label>
						{showPreviousPatchDeliveryTime && (
							<span className='inline-flex shrink-0 items-center whitespace-nowrap rounded bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 border border-blue-100'>
								{previousPatchDeliveryTime}
							</span>
						)}
					</div>

					<div className='pt-1 pb-1.5 normal-case'>
						<SegmentedChoice
							ariaLabel='Are previous patches returned?'
							disabled={disabled}
							options={[
								{ value: 'yes', label: 'Yes', activeClass: 'bg-green-600 text-white' },
								{ value: 'no', label: 'No', activeClass: 'bg-red-600 text-white' },
								{ value: 'na', label: 'N/A', activeClass: 'bg-gray-600 text-white' },
							]}
							value={selectedValue}
							onChange={handleAnswer}
						/>

						{selectedValue === 'yes' && (
							<div className='mt-1.5'>
								<p className='mb-0.5 text-[11px] font-medium text-gray-600'>How many came back?</p>
								{/* Partial opens a count box beside the choice. With no expected count known,
									there is nothing for All to fill in, so the box stands alone. */}
								<div className='flex items-center gap-1.5'>
									{expectedCount !== null && (
										<SegmentedChoice
											ariaLabel='How many patches came back'
											className={returnMode === 'other' ? 'flex-[2]' : 'flex-1'}
											disabled={disabled}
											options={[
												{ value: 'all', label: `All (${expectedCount})`, activeClass: 'bg-blue-600 text-white' },
												{ value: 'other', label: 'Partial', activeClass: 'bg-orange-500 text-white' },
											]}
											value={returnMode}
											onChange={handleModeChange}
										/>
									)}
									{(returnMode === 'other' || expectedCount === null) && (
										<input
											autoFocus
											aria-label='How many patches returned'
											type='text'
											inputMode='numeric'
											pattern='[0-9]*'
											placeholder='Qty'
											value={returnedCount}
											onChange={handleCountChange}
											disabled={disabled}
											className={`h-8 min-w-0 flex-1 rounded-md border bg-white px-2 text-center text-sm font-semibold outline-none ${overExpected ? 'border-red-600 text-red-700' : 'border-orange-500 text-gray-900'}`}
										/>
									)}
								</div>
							</div>
						)}
					</div>

					{selectedValue === 'yes' && overExpected && (
						<p className='pb-2 text-xs font-medium normal-case text-red-600'>
							Can't be more than expected ({expectedCount}).
						</p>
					)}

				</div>

				{/* {showToast.successToast &&
					<div className='fixed  left-0 w-full bg-[#10b981] text-white text-center py-2 z-50'>
						<SuccessMessage message={'Updated Successfully'} />
					</div>} */}

				{showToast.errorToast &&
					<div className='fixed  left-0 w-full  text-white text-center py-2 z-50'>
						<ErrorMessage message={'Oops!.Got an Issue.please try again.'} />
					</div>}
			</div>
		</React.Fragment>
	);
};


// A row of joined buttons where one is picked; each option has its own picked color,
// so the Yes / No / N/A and All / Partial rows don't look alike.
const SegmentedChoice = ({ options, value, onChange, disabled = false, ariaLabel, className = '' }) => (
	<div role='group' aria-label={ariaLabel} className={`flex h-8 overflow-hidden rounded-md border border-gray-400 bg-white ${className}`}>
		{options.map((option, position) => (
			<button
				key={option.value}
				type='button'
				aria-pressed={value === option.value}
				disabled={disabled}
				onClick={() => onChange(option.value)}
				className={`min-w-0 flex-1 whitespace-nowrap text-[13px] font-semibold normal-case ${position > 0 ? 'border-l border-gray-400' : ''} ${value === option.value ? option.activeClass : 'bg-white text-gray-900'}`}
			>
				{option.label}
			</button>
		))}
	</div>
);

export const DoseFrequencySelector = ({
	dilaudidDelivery,
	selectedDoses = [],
	setSelectedDoses,
	previous_delivery_time_diff = null,
	minutes_until_can_deliver = null,
	disabled = false,
	winrxDataId = null,
	onAllDosesUnavailable = () => { }
}) => {
	// Normalize delivery_status for comparison (API may return "delivered" or "Delivered")
	const isDeliveredStatus = (d) => String(d?.delivery_status || '').toLowerCase() === 'delivered'

	// A selection is only still current if the dose gap prompt on it is too. The
	// prompt appears and changes as doses go out - delivering dose 1 is what gives
	// dose 2 its prompt - so comparing dose ids alone kept a copy taken before the
	// prompt existed and the driver was never asked.
	const promptKey = (prompt) => prompt
		? [prompt.requires_confirmation ? 1 : 0, prompt.can_override ? 1 : 0, prompt.next_allowed_at || ''].join('|')
		: ''
	const allDosesDeliveredForTimer = dilaudidDelivery?.length > 0 && dilaudidDelivery.every((d) => isDeliveredStatus(d))
	const hasAnyDeliveredDose = dilaudidDelivery?.some((d) => isDeliveredStatus(d))
	const parsedMinutesUntil = parseMinutesUntilCanDeliver(minutes_until_can_deliver)
	// Show timer when we have minutes_until_can_deliver and there was a previous delivery (rx shows "Previous dose delivered") or any dose in list is delivered; and not all doses are delivered yet
	const showCannotDeliverTimer = parsedMinutesUntil != null && (minutes_until_can_deliver != null && minutes_until_can_deliver !== '') && dilaudidDelivery?.length > 0 && !allDosesDeliveredForTimer && (!!previous_delivery_time_diff || hasAnyDeliveredDose)

	// Live countdown: target time from minutes_until_can_deliver (minutes from when data was received)
	const [timerRemainingMinutes, setTimerRemainingMinutes] = useState(null)
	useEffect(() => {
		if (!showCannotDeliverTimer || parsedMinutesUntil == null) {
			setTimerRemainingMinutes(null)
			return
		}
		const num = parsedMinutesUntil
		if (num < 0) {
			setTimerRemainingMinutes(0)
			return
		}
		const targetTime = Date.now() + num * 60 * 1000
		const tick = () => {
			const remaining = (targetTime - Date.now()) / 60000
			if (remaining <= 0) {
				setTimerRemainingMinutes(0)
				return
			}
			setTimerRemainingMinutes(remaining)
		}
		tick()
		const interval = setInterval(tick, 1000)
		return () => clearInterval(interval)
	}, [showCannotDeliverTimer, parsedMinutesUntil])
	// can_deliver logic only when status is not delivered
	const allDosesDelivered = dilaudidDelivery?.length > 0 &&
		dilaudidDelivery.every((d) => isDeliveredStatus(d))
	// Step 1: all can_deliver false → entire section read-only (only when not all doses delivered)
	const allCanDeliverFalse = !allDosesDelivered && dilaudidDelivery?.length > 0 &&
		dilaudidDelivery.every((d) => d?.can_deliver === false)
	// Step 2: all can_deliver true → first checkbox read-only (only when not all doses delivered)
	const allCanDeliverTrue = !allDosesDelivered && dilaudidDelivery?.length > 0 &&
		dilaudidDelivery.every((d) => d?.can_deliver === true)

	// Both callbacks below write a brand new object or array into state that is
	// shared across the whole patient list, so calling either one when nothing
	// has actually changed still re-renders this component. This effect depends
	// on those callbacks, whose identity changes on every render, so it would
	// then run again and write again - a loop that shows up as a dose checkbox
	// flickering on and off. Every write here has to be conditional.
	const reportedUnavailableRef = useRef(null)

	useEffect(() => {
		if (disabled) return
		if (!dilaudidDelivery?.length) return
		if (allCanDeliverFalse) {
			if (selectedDoses?.length) setSelectedDoses([])
			if (winrxDataId != null && reportedUnavailableRef.current !== winrxDataId) {
				reportedUnavailableRef.current = winrxDataId
				onAllDosesUnavailable(winrxDataId)
			}
			return
		}
		// Deliverable again: let the Rx be reported unavailable if it flips back.
		reportedUnavailableRef.current = null
		// Keep selected dose IDs matched to only doses that can be delivered now.
		// Delivered doses are shown as history only and are not submitted again.
		const deliverableDoses = dilaudidDelivery
			.filter((d) => !isDeliveredStatus(d) && d?.can_deliver === true)
			.map((d) => ({
				frequency: d?.frequency,
				dose_id: d?.id,
				dose_no: d?.dose_no,
				dose_gap_prompt: d?.dose_gap_prompt,
			}))
		const currentMatches = selectedDoses?.length === deliverableDoses.length &&
			deliverableDoses.every((d) => selectedDoses.some((s) => s.dose_id === d.dose_id
				&& promptKey(s.dose_gap_prompt) === promptKey(d.dose_gap_prompt)))
		if (!currentMatches) {
			setSelectedDoses(deliverableDoses)
		}
	}, [dilaudidDelivery, selectedDoses?.length, setSelectedDoses, disabled, allCanDeliverFalse, winrxDataId, onAllDosesUnavailable])

	const handleDoseToggle = useCallback((dose, index) => {
		if (disabled) return
		if (allCanDeliverFalse) return
		if (isDeliveredStatus(dose)) return
		if (allCanDeliverTrue && index === 0) return
		if (dose?.can_deliver === false) return
		// Any dose with can_deliver === true is locked (checked + disabled), user cannot uncheck
		if (dose?.can_deliver === true) return

		const isAlreadySelected = selectedDoses?.some((d) => d?.dose_id === dose?.id)
		if (isAlreadySelected) {
			setSelectedDoses((selectedDoses || []).filter((d) => d.dose_no < dose.dose_no))
		} else {
			setSelectedDoses(
				dilaudidDelivery.slice(0, index + 1).map((d) => ({
					frequency: d.frequency,
					dose_id: d.id,
					dose_no: d.dose_no,
					dose_gap_prompt: d.dose_gap_prompt,
				}))
			)
		}
	}, [disabled, selectedDoses, dilaudidDelivery, setSelectedDoses, allCanDeliverFalse, allCanDeliverTrue])

	const isDoseSelected = useCallback(
		(dose_id) => selectedDoses?.some((d) => d.dose_id === dose_id),
		[selectedDoses]
	)

	if (!dilaudidDelivery || dilaudidDelivery?.length === 0) {
		return null
	}

	const sectionReadOnly = disabled || allCanDeliverFalse

	return (
		<div className={`mt-2 px-1 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded border border-blue-200 ${sectionReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
			<div className="gap-1 flex flex-wrap">
				{dilaudidDelivery.filter(Boolean).map((delivery, index) => {
					const isSelected = isDoseSelected(delivery.id)
					const isDoseOne = index === 0
					const isDelivered = isDeliveredStatus(delivery)
					const isChecked = isDelivered || isSelected
					const deliveryTimeDisplay = delivery?.delivery_time_display
					// Delivered → disabled; can_deliver false → disabled (unselectable); can_deliver true → disabled (checked and locked, user cannot uncheck); first when all true → read-only
					const isDisabled = isDelivered || allCanDeliverFalse || (isDoseOne && allCanDeliverTrue) || delivery?.can_deliver === false || delivery?.can_deliver === true

					return (
						<div key={delivery.id} className="inline-flex flex-col items-start">
							<div
								onClick={() => handleDoseToggle(delivery, index)}
								className={`
									flex items-center gap-1 px-2 py-1 rounded border transition-all duration-200
									${isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:border-blue-300"}
									${isDelivered ? "bg-green-50 border-green-200 text-green-700" : isSelected ? "bg-blue-100 border-blue-200 text-blue-600" : "bg-grey-100 border-gray-200 text-gray-700"}
								`}
								style={{ width: 'fit-content' }}
							>
								<div
									className={`
										w-4 h-4 rounded border flex items-center justify-center
										${isChecked ? "border-white bg-white" : "border-gray-200 bg-white"}
									`}
								>
									{isChecked && (
										<svg
											className={`w-2.5 h-2.5 ${isDelivered ? "text-green-600" : "text-blue-600"}`}
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clipRule="evenodd"
											/>
										</svg>
									)}
								</div>
								<span
									className={`text-[12px] font-medium ${isDelivered ? "text-green-700" : isSelected ? "text-blue-600" : "text-gray-700"}`}
								>
									{delivery.dose} {delivery?.intervention_iv_code === 'SA' ? '(C)' : ''}
								</span>
							</div>
							{isDelivered && deliveryTimeDisplay && (
								<span className="mt-0.5 pl-1 text-[10px] font-medium leading-none text-green-700">
									{deliveryTimeDisplay}
								</span>
							)}
						</div>
					)
				})}
			</div>


			{previous_delivery_time_diff && showCannotDeliverTimer && (timerRemainingMinutes === null ? parsedMinutesUntil > 0 : timerRemainingMinutes > 0) && (
				<div className="text-xs text-gray-600 mt-1">
					Next delivery in {formatMinutesSecondsCompact(timerRemainingMinutes != null ? timerRemainingMinutes : parsedMinutesUntil)}
				</div>
			)}
		</div>
	)
}
